'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Head from 'next/head';
import UseCasesSidebar from './components/UseCasesSidebar';
import Footer from '../components/Footer';
import { allUseCases as useCases } from './all';
import type { UseCase } from '@/types/use-case';

// Get unique industries for stats
const uniqueIndustries = Array.from(new Set(useCases.map(uc => uc.industry)));

// Schema.org structured data for the use cases overview page
const useCasesSchemaData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Real-World Use Cases - Tomorrow.io Weather API',
  description: 'Practical implementation examples showing how to integrate Tomorrow.io Weather API with popular frameworks, libraries, and business systems.',
  url: 'https://llm-docs.tomorrow.io/use-cases',
  mainEntity: {
    '@type': 'ItemList',
    name: 'Weather API Use Cases',
    description: 'Collection of real-world implementation examples for Tomorrow.io Weather API',
    numberOfItems: useCases.length,
    itemListElement: useCases.map((useCase, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'SoftwareApplication',
        name: useCase.title,
        description: useCase.description,
        applicationCategory: useCase.industry,
        keywords: useCase.tags.join(', '),
        url: `https://llm-docs.tomorrow.io/use-cases/${useCase.id}`,
        programmingLanguage: useCase.tags.filter(tag => 
          ['JavaScript', 'TypeScript', 'Python', 'React', 'Node.js', 'Vue.js', 'Angular'].includes(tag)
        ),
        operatingSystem: 'Cross-platform',
        softwareHelp: {
          '@type': 'CreativeWork',
          name: `${useCase.title} Implementation Guide`,
          description: `Step-by-step guide for implementing ${useCase.title}`,
        }
      }
    }))
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Documentation',
        item: 'https://llm-docs.tomorrow.io'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Use Cases',
        item: 'https://llm-docs.tomorrow.io/use-cases'
      }
    ]
  },
  publisher: {
    '@type': 'Organization',
    name: 'Tomorrow.io',
    url: 'https://tomorrow.io'
  }
};

export default function UseCasesPage() {
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarHidden(!isSidebarHidden);
  };

  // Group use cases by industry
  const categories = Object.values(useCases.reduce((acc, uc) => {
    if (!acc[uc.industry]) acc[uc.industry] = { industry: uc.industry, items: [] as UseCase[] };
    acc[uc.industry].items.push(uc);
    return acc;
  }, {} as Record<string, { industry: string; items: UseCase[] }>));

  return (
    <>
      {/* Schema.org JSON-LD */}
      <Head>
        <title>Tomorrow.io LLM-Friendly Weather API Documentation | Use Cases</title>
        <meta name="description" content="Practical implementation examples showing how to integrate Tomorrow.io Weather API with popular frameworks, libraries, and business systems." />
      </Head>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(useCasesSchemaData, null, 2),
        }}
      />

      {/* Header */}
      <section className="page-header py-8 text-center border-b-2 border-[#e6f0ff] mb-8">
        <h1>
          Real-World Use Cases
        </h1>
        <p className="text-xl text-[#666666] max-w-3xl mx-auto">
          Practical implementation examples showing how to integrate Tomorrow.io Weather API 
          with popular frameworks, libraries, and business systems.
        </p>
      </section>
      
      <main className="container mx-auto px-4 flex">
        {!isSidebarHidden && (
          <div className="sidebar-container w-80 flex-shrink-0">
            <UseCasesSidebar categories={categories} toggleSidebar={toggleSidebar} />
          </div>
        )}
        
        <div className={`content-area transition-all duration-300 ${isSidebarHidden ? 'w-full' : 'flex-grow'} min-w-0`}>
          {isSidebarHidden && (
            <div className="flex justify-start mb-6 pb-4">
              <button 
                onClick={toggleSidebar}
                className="show-sidebar-btn w-14 h-8 bg-primary hover:bg-primary-dark text-white rounded-full flex items-center justify-center shadow-md transition-colors duration-200"
                style={{ width: '26px', height: '26px' }}
                aria-label="Show Sidebar"
                title="Show Sidebar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V3zm3 0v14h10V3H6z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
          
          <div className="space-y-12">

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
          <div className="text-3xl font-bold text-blue-600 mb-2">{useCases.length}</div>
          <div className="text-gray-600">Use Cases</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
          <div className="text-3xl font-bold text-green-600 mb-2">{uniqueIndustries.length}</div>
          <div className="text-gray-600">Industries</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
          <div className="text-3xl font-bold text-purple-600 mb-2">
            {Array.from(new Set(useCases.flatMap(uc => uc.tags))).length}
          </div>
          <div className="text-gray-600">Technologies</div>
        </div>
      </div>

      {/* All Use Cases */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          All Use Cases
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {useCases.map((useCase) => (
            <Link 
              key={useCase.id} 
              href={`/use-cases/${useCase.id}`}
              className="block group"
            >
              <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-200 h-full">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {useCase.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {useCase.description}
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ml-2 ${
                    useCase.complexity === 'Basic' ? 'bg-green-100 text-green-800' :
                    useCase.complexity === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {useCase.complexity}
                  </span>
                </div>

                {/* Key Metrics */}
                <div className="space-y-2 mb-4">
                  {useCase.timeToImplement && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Time to Build:</span>
                      <span className="font-medium">{useCase.timeToImplement}</span>
                    </div>
                  )}
                </div>

                {/* Business Benefits */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Key Benefits</h4>
                  <div className="space-y-1">
                    {useCase.businessMetrics.slice(0, 2).map((metric, index) => (
                      <div key={index} className="text-xs text-gray-600">
                        • {metric.title}: <span className="font-medium">{metric.impact}</span>
                      </div>
                    ))}
                    {useCase.businessMetrics.length > 2 && (
                      <div className="text-xs text-gray-500">
                        +{useCase.businessMetrics.length - 2} more benefits
                      </div>
                    )}
                  </div>
                </div>

                {/* Technologies and Industry */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {/* Industry Tag */}
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-700 font-medium">
                    {useCase.industry}
                  </span>
                  {/* Technology Tags */}
                  {useCase.tags.slice(0, 2).map((tag) => (
                    <span 
                      key={tag}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                    >
                      {tag}
                    </span>
                  ))}
                  {useCase.tags.length > 2 && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                      +{useCase.tags.length - 2} more
                    </span>
                  )}
                </div>

                {/* Call to Action */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-sm text-gray-600">View Implementation</span>
                  <svg 
                    className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

            {/* Coming Soon */}
            <section className="text-center py-12 bg-gray-50 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">More Use Cases Coming Soon</h2>
              <p className="text-gray-600 mb-6">
                We're actively developing more real-world examples across industries including:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="text-sm text-gray-700">🛒 E-commerce</div>
                <div className="text-sm text-gray-700">🏠 Smart Homes</div>
                <div className="text-sm text-gray-700">🎉 Event Planning</div>
                <div className="text-sm text-gray-700">🚚 Logistics</div>
                <div className="text-sm text-gray-700">☀️ Energy</div>
                <div className="text-sm text-gray-700">🏗️ Construction</div>
                <div className="text-sm text-gray-700">🚗 Transportation</div>
                <div className="text-sm text-gray-700">📱 Mobile Apps</div>
              </div>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  );
} 